import React from 'react';
import { Plus, Copy, Clipboard, Users, Download, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PlatoSyncButton from './PlatoSyncButton';
import { formatDateToString } from '@/lib/dateUtils';

interface DailyViewActionsProps {
  onAddSchedule: () => void;
  onCopyDay: () => void;
  onPasteDay: () => void;
  onShowManpowerSchedule: () => void;
  onShowBackupDASchedule: () => void;
  onExtractAsImage: () => void;
  copiedItemsCount: number;
  currentDate: Date;
  onRefreshSchedule?: () => void;
}

const DailyViewActions = ({
  onAddSchedule,
  onCopyDay,
  onPasteDay,
  onShowManpowerSchedule,
  onShowBackupDASchedule,
  onExtractAsImage,
  copiedItemsCount,
  currentDate,
  onRefreshSchedule,
}: DailyViewActionsProps) => {
  const { currentClinic } = useClinic();
  const isAllClinicsView = !currentClinic;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  onClick={onAddSchedule}
                  className="flex items-center space-x-2"
                  size="sm"
                  disabled={isAllClinicsView}
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Add Schedule</span>
                  <span className="sm:hidden">Add</span>
                </Button>
              </span>
            </TooltipTrigger>
            {isAllClinicsView && (
              <TooltipContent>
                <p>Please select a specific clinic to add schedules</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        <Button
          variant="outline"
          onClick={onCopyDay}
          className="flex items-center space-x-1 sm:space-x-2"
          size="sm"
        >
          <Copy className="h-4 w-4" />
          <span className="hidden sm:inline">Copy Day</span>
          <span className="sm:hidden">Copy</span>
        </Button>

        {copiedItemsCount > 0 && (
          <Button
            variant="outline"
            onClick={onPasteDay}
            className="flex items-center space-x-1 sm:space-x-2"
            size="sm"
          >
            <Clipboard className="h-4 w-4" />
            <span>Paste ({copiedItemsCount})</span>
          </Button>
        )}

        <Button
          variant="outline"
          onClick={onShowManpowerSchedule}
          className="flex items-center space-x-1 sm:space-x-2"
          size="sm"
        >
          <Users className="h-4 w-4" />
          <span className="hidden sm:inline">Daily Manpower Schedule</span>
          <span className="sm:hidden">Manpower</span>
        </Button>

        <Button
          variant="outline"
          onClick={onShowBackupDASchedule}
          className="flex items-center space-x-1 sm:space-x-2"
          size="sm"
        >
          <UserCog className="h-4 w-4" />
          <span className="hidden sm:inline">Backup DA Schedule</span>
          <span className="sm:hidden">Backup DA</span>
        </Button>

        <Button
          variant="outline"
          onClick={onExtractAsImage}
          className="flex items-center space-x-1 sm:space-x-2"
          size="sm"
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">Extract as Image</span>
          <span className="sm:hidden">Export</span>
        </Button>

        {/* Plato Sync Button - Only show for Forum clinic */}
        {currentClinic?.code?.toLowerCase() === 'forum' && (
          <PlatoSyncButton
            syncType="day"
            date={formatDateToString(currentDate)}
            clinicId={currentClinic.id}
            onRefreshSchedule={onRefreshSchedule}
          />
        )}
      </div>

      <div className="text-xs sm:text-sm text-muted-foreground shrink-0">
        {currentDate.toLocaleDateString('en-SG', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
      </div>
    </div>
  );
};

export default DailyViewActions;
